import React from 'react'
import { SafetyOutlined, KeyOutlined, GlobalOutlined } from '@ant-design/icons'

import { useLanguage } from 'context/LanguageContext'

import { ReviewFormCard, ReviewList, useReviews } from './UserReviews'

import translations, { Translations } from './translations'

import s from './TrustSignals.module.scss'

// Ссылки на соцсети авторов отзывов (одинаковы для всех языков).
const TESTIMONIAL_1_LINK = 'https://www.instagram.com/amysavas?utm_source=qr'
const TESTIMONIAL_2_LINK = 'https://www.facebook.com/share/1FA7qddvKe/'

/**
 * Trust Signals блок для главной — между Banner и WhatIs.
 *
 * Структура:
 *   1. Заголовок секции
 *   2. Полоса из 4 чисел (биржи / сделки / языки / годы)
 *   3. 3 пункта о безопасности (API без withdraw / TronLink / open-source)
 *   4. 2 отзыва + блок-форма «Оставьте отзыв» (3-я колонка)
 *
 * Цель: за один экран снять основные возражения нового посетителя:
 *   - «вам можно доверять?» → бэджи + опен-сорс
 *   - «вы серьёзные?»       → числа + годы на рынке
 *   - «это работает?»        → отзывы реальных пользователей
 */
export const TrustSignals: React.FC = React.memo(() => {
    const { language } = useLanguage()
    const t: Translations = translations[language] || translations.en
    // Живые отзывы: пишутся и хранятся у нас, а не во внешней Google-форме.
    const reviews = useReviews()

    const stats = [
        { value: t.statExchangesValue, label: t.statExchangesLabel },
        { value: t.statTradesValue, label: t.statTradesLabel },
        { value: t.statLanguagesValue, label: t.statLanguagesLabel },
        { value: t.statYearsValue, label: t.statYearsLabel },
    ]

    const badges = [
        { icon: KeyOutlined, title: t.badge1Title, desc: t.badge1Desc },
        { icon: SafetyOutlined, title: t.badge2Title, desc: t.badge2Desc },
        { icon: GlobalOutlined, title: t.badge3Title, desc: t.badge3Desc },
    ]

    const testimonials = [
        {
            quote: t.testimonial1Quote,
            author: t.testimonial1Author,
            country: t.testimonial1Country,
            link: TESTIMONIAL_1_LINK,
        },
        {
            quote: t.testimonial2Quote,
            author: t.testimonial2Author,
            country: t.testimonial2Country,
            link: TESTIMONIAL_2_LINK,
        },
    ]

    return (
        <div className={s.wrapper}>
            <div className={s.header}>
                <h2>{t.sectionTitle}</h2>
                <p>{t.sectionSubtitle}</p>
            </div>

            {/* ─── Числа: одна полоса, без карточек ─────────────── */}
            <dl className={s.stats}>
                {stats.map(({ value, label }) => (
                    <div className={s.stat} key={label}>
                        <dt className={s.label}>{label}</dt>
                        <dd className={s.value}>{value}</dd>
                    </div>
                ))}
            </dl>

            {/* ─── Безопасность ─────────────────────────────────── */}
            <div className={s.badgesSection}>
                <h3>{t.badgesTitle}</h3>
                <ul className={s.badges}>
                    {badges.map(({ icon: Icon, title, desc }) => (
                        <li className={s.badge} key={title}>
                            <span className={s.badgeIcon}>
                                <Icon />
                            </span>
                            <h4>{title}</h4>
                            <p>{desc}</p>
                        </li>
                    ))}
                </ul>
            </div>

            {/* ─── Отзывы ───────────────────────────────────────── */}
            <div className={s.testimonialsSection}>
                <h3>{t.testimonialsTitle}</h3>
                <div className={s.testimonials}>
                    {testimonials.map(({ quote, author, country, link }) => (
                        <figure className={s.testimonial} key={author}>
                            <blockquote className={s.quote}>{quote}</blockquote>
                            <figcaption className={s.author}>
                                <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {author}
                                </a>
                                <span className={s.country}>{country}</span>
                            </figcaption>
                        </figure>
                    ))}
                    <ReviewFormCard
                        t={t}
                        isAuthorized={reviews.isAuthorized}
                        onPosted={() => reviews.load(0)}
                    />
                </div>

                {/* Отзывы, оставленные посетителями прямо на сайте. */}
                <ReviewList
                    t={t}
                    items={reviews.items}
                    total={reviews.total}
                    page={reviews.page}
                    loading={reviews.loading}
                    onLoad={reviews.load}
                />
            </div>
        </div>
    )
})
